import { useEffect, useRef, useState } from 'react';
import { useRefetch } from './useRefetch';
import { useIsFocusedSafe } from '@v2/pages/common/hooks/useIsFocusedSafe';

interface UseFetchParams<T> {
  fetchFn: (signal: AbortSignal) => Promise<T | null>;
  deps?: unknown[];
}

export const useFetch = <T>({ fetchFn, deps = [] }: UseFetchParams<T>) => {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const { triggerRefetch, refetch } = useRefetch();
  const fetchFnRef = useRef(fetchFn);
  fetchFnRef.current = fetchFn;
  const isFocused = useIsFocusedSafe();
  const hasMountedRef = useRef(false);

  useEffect(() => {
    if (!isFocused) return;

    const abortController = new AbortController();

    (async () => {
      try {
        setLoading(true);
        const result = await fetchFnRef.current(abortController.signal);
        if (!abortController.signal.aborted) {
          setData(result);
        }
      } catch (e) {
        if (!abortController.signal.aborted) {
          console.error(e);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    })();

    return () => {
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, triggerRefetch]);

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    if (isFocused) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFocused]);

  return { data, loading, refetch };
};
